<template>
    <div class="feedback p-4">
        
        <div v-if="!filteredIssues.length">
            <p class="text-center">{{ $t('issues.no-issue') }}</p>
        </div>
        
        <div v-else class="timeline timeline-one-side" data-timeline-content="axis" data-timeline-axis-style="dashed">
            <div v-for="(item, index) in filteredIssues" :key="index">
                <div class="timeline-block"
                    :class="{'timeslot-block': !item.author_id, 'issue-block': item.author_id}">

                    <span class="timeline-step badge-default">
                        <i class="fas fa-thumbtack"></i>
                    </span>
                    
                    <div class="timeline-content">

                        <issue v-if="item.author_id"
                            :issue="item"
                            :scope="scope"
                            :lang="registration.degree.lang"
                            :edit="edit"
                            @update="update"
                            @remove="remove"
                            @save="save"
                        >
                        </issue>

                        <timeslot-issues v-if="!item.author_id"
                            :timeslot="item"
                            :scope="scope"
                            :lang="registration.degree.lang"
                            :edit="edit"
                            @update="update"
                            @remove="remove" 
                            @save="save"
                        >
                        </timeslot-issues>
                    </div>
                </div>
            </div>
        </div>

        <div class="text-center mt-4" v-if="edit">
            <base-button type="primary" @click.prevent="create()">
                <i class="fas fa-plus mr-1"></i> {{ $t('common.add') }}
            </base-button>

            <base-button type="primary" :loading="sending" @click.prevent="sendIssues" v-if="mustSendIssues">
                <i class="fas fa-paper-plane mr-1"></i> {{ $t('feedbacks.send-now') }}
            </base-button>

            <base-button type="primary" :loading="sending" @click.prevent="sendIssues" v-if="mayResendIssues">
                <i class="fas fa-paper-plane mr-1"></i> {{ $t('feedbacks.resend-now') }}
            </base-button>
        </div>
        <div class="text-center pt-2" v-if="mustSendIssues">
            <small>{{ $t('feedbacks.send-now-notice') }}</small>
        </div>

        <portal to="modals">

            <form-modal v-model="modals.editIssue"
                :title="$t('common.issue')"
                :loading="issueForm.loading"
                size="lg"
                :centered="false"
            >
                <issue-form v-model="issueForm.data"
                    :errors="issueForm.errors"
                    :modal="modals.editIssue"
                    :timeslot="timeslot"
                >
                </issue-form>
            </form-modal>

            <confirm-modal v-model="modals.confirmDelete">
                <p>{{ $t('issues.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="modals.confirmSend">
                <p>{{ $t('feedbacks.send-now-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CrudForm from '@trax/classes/CrudForm'
    import CrudClient from '@trax/classes/CrudClient'
    import IssueForm from '../common/IssueForm'
    import Issue from '../common/Issue'
    import TimeslotIssues from './TimeslotIssues'
    import moment from 'moment'

    export default {

        components: {
            IssueForm, Issue, TimeslotIssues
        },

        model: {
            prop: 'session',
            event: 'change'
        },

        props: {
            
            registration: {
                type: Object,
                required: true
            },
            
            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },
            
            edit: {
                type: Boolean,
                default: false
            },
            
            role: {
                type: String,
                default: null
            },
            
            scope: {
                type: String,
                default: 'mine'
            },
        },

        data() {
            let endpoint = '/astral/api/front/dashboard/issues'
            let client = new CrudClient(endpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
                role: this.role,
                scope: this.scope,
            })
            return {
                endpoint,
                client,
                issueForm: new CrudForm(client, this.mapData, this.unmapData),
                modals: new ModalsController(['confirmDelete', 'confirmSend', 'editIssue']),
                issues: [],
                loaded: false,
                timeslot: null,
                sending: false,
                moment: moment,
            }
        },

        created() {
            this.issueForm.init(this, 'issueForm')
            this.modals.init(this, 'modals')
            this.list()
        },

        computed: {

            filteredIssues() {
                return this.issues.filter(item => item.author_id || (item.issues && item.issues.length))
            },

            mustSendIssues() {
                if (this.$permissions.hasRight('dashboard.pilot-log.access') && this.filteredIssues.length > 0) {
                    return true
                }
                return this.edit && this.session && this.session.issued_at == null && this.filteredIssues.length > 0
            },

            mayResendIssues() {
                return this.edit && this.session && this.session.issued_at && this.filteredIssues.length > 0
            }
        },

        watch: {
            session() {
                this.$emit('mustSendIssues', this.mustSendIssues)
            },

            filteredIssues(issues) {
                this.$emit('loaded', issues.length)
            }
        },

        methods: {

            sendIssues() {
                this.modals.open('confirmSend').then(resp => {
                    if (resp.confirmed) {
                        this.sending = true
                        if (this.$permissions.hasRight('dashboard.pilot-log.access')) {
                            // Pilot. Just a notification.
                            axios.post('/astral/api/front/dashboard/registrations/'+this.registration.id+'/simulations/'+this.simulation.id+'/issue')
                            .then(resp => {
                                this.sending = false
                                this.$notify({type: 'primary', message: this.$t('feedbacks.sent') })
                            }).catch(error => {
                                this.sending = false
                                if (error.response.status == 404) {
                                    this.$notify({type: 'danger', message: this.$t('feedbacks.send-failed') })
                                } else if (error.response.status == 500 && error.response.data.message) {
                                    this.$notify({type: 'danger', message: error.response.data.message })
                                } else {
                                    this.$auth.handleErrorVm(error, this)
                                }
                            });
                        } else {
                            // Instructor. Will change the session status.
                            axios.post('/astral/api/front/dashboard/sessions/'+this.session.id+'/issue')
                            .then(resp => {
                                this.sending = false
                                this.$notify({type: 'primary', message: this.$t('feedbacks.sent') })
                                this.$emit('change', resp.data.data)
                            }).catch(error => {
                                this.sending = false
                                if (error.response.status == 404) {
                                    this.$notify({type: 'danger', message: this.$t('feedbacks.send-failed') })
                                } else if (error.response.status == 500 && error.response.data.message) {
                                    this.$notify({type: 'danger', message: error.response.data.message })
                                } else {
                                    this.$auth.handleErrorVm(error, this)
                                }
                            });
                        }
                    }
                })
            },

            list() {
                this.loaded = false
                this.client.list().then(resp => {          
                    this.issues = resp.data.data
                    this.loaded = true
                    this.$emit('mustSendIssues', this.mustSendIssues)
                })
            },

            create() {
                this.timeslot = null
                this.modals.open('editIssue')
                this.issueForm.create().then(resp => {
                    this.modals.close('editIssue')
                    this.list()
                })
            },

            createInTimeslot(timeslot) {
                this.timeslot = timeslot
                this.modals.open('editIssue')
                this.issueForm.create().then(resp => {
                    this.modals.close('editIssue')
                    this.list()
                })
            },

            update(issue) {
                this.modals.open('editIssue')
                this.issueForm.update(issue).then(resp => {
                    this.modals.close('editIssue')
                    this.list()
                })
            },

            remove(issue) {
                this.modals.open('confirmDelete').then(resp => {
                    if (resp.confirmed) {
                        this.client.delete(issue.id).then(resp => {
                            this.list()
                        })
                    }
                })
            },

            save(issue) {
                this.client.update(issue).then(resp => {
                    this.list()
                })
            },

            mapData(issue) {
                return {
                    id: issue ? issue.id : null,
                    log_time_from: issue ? issue.log_time_from : null,
                    log_time_to: issue ? issue.log_time_to : null,
                    entered_at: issue ? issue.entered_at : null,
                    summary: issue ? issue.summary : null,
                    description: issue ? issue.description : null,
                    timeslot_id: issue ? issue.timeslot_id : (this.timeslot ? this.timeslot.id : null),
                }
            },

            unmapData(data) {
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.author_role = this.role
                data.entered_at = this.moment().format('YYYY-MM-DD HH:mm:ss')
                return data
            }
        }
    }
</script>

<style scoped>

    .description {
        white-space: pre-wrap;
    }
    @media (min-width: 992px) {
        .timeline-one-side .timeline-content {
            max-width: none;
        }
    }
    .timeslot-block {
        margin-bottom: 0;
    }
    .issue-block {
        margin-bottom: 20px;
    }

</style>
